import { type Metric } from '@/types';
import { type ParsedChartReturn } from '@tallinn-arvudes/ui/chart/types';

/**
 * Filters chart data to get actual data points (excluding chart-only padding and interpolated values)
 */
function getActualDataPoints(parsedMetric: ParsedChartReturn) {
  // Skip the first and last elements from chartData since they are only for the chart
  // Also filter out interpolated values
  return parsedMetric.chartData
    .slice(1, -1)
    .filter((point) => !point.isInterpolated);
}

export function generateCsv(parsedMetric: ParsedChartReturn) {
  const headers = [
    'Year',
    'Real Value',
    'Target Value',
    'Target Value Indicator',
  ];

  const dataPoints = getActualDataPoints(parsedMetric);

  const chartRows = dataPoints.map((point) => {
    const year = point.xAxis || '';
    const realValue = point.realValue ?? '';
    const targetValue = point.targetValue ?? '';
    const targetIndicator = point.targetValueIndicator ?? '';
    return `"${year}","${realValue}","${targetValue}","${targetIndicator}"`;
  });

  return [headers.join(','), ...chartRows].join('\n');
}

export function generateXml(
  metric: Metric,
  parsedMetric: ParsedChartReturn,
): string {
  try {
    const doc = document.implementation.createDocument('', '', null);

    const xmlDeclaration = '<?xml version="1.0" encoding="UTF-8"?>\n';

    const metricElement = doc.createElement('metric');
    const metadataElement = doc.createElement('metadata');

    const addElement = (
      parent: Element,
      name: string,
      value: string | null | undefined,
    ) => {
      const element = doc.createElement(name);
      element.textContent = value ?? '';
      parent.appendChild(element);
      return element;
    };

    addElement(metadataElement, 'id', String(metric.id ?? ''));
    addElement(metadataElement, 'type', String(metric.typeName ?? ''));
    addElement(metadataElement, 'title', String(metric.titlePlainText ?? ''));
    addElement(metadataElement, 'unit', String(metric.unit ?? ''));
    addElement(
      metadataElement,
      'description',
      String(metric.descriptionPlainText ?? ''),
    );
    addElement(metadataElement, 'dataSource', String(metric.dataSource ?? ''));
    addElement(
      metadataElement,
      'monitoringPeriod',
      String(metric.monitoringPeriod ?? ''),
    );
    addElement(
      metadataElement,
      'responsibleInstitutionName',
      String(metric.responsibleInstitutionName ?? ''),
    );
    addElement(
      metadataElement,
      'responsibleInstitutionLink',
      String(metric.responsibleInstitutionLink ?? ''),
    );
    addElement(
      metadataElement,
      'methodology',
      String(metric.methodologyPlainText ?? ''),
    );
    addElement(metadataElement, 'code', String(metric.code ?? ''));
    addElement(metadataElement, 'focusArea', String(metric.focusArea ?? ''));
    addElement(
      metadataElement,
      'targetLevel',
      String(metric.targetLevel ?? ''),
    );
    addElement(
      metadataElement,
      'targetLevelDate',
      String(metric.targetLevelDate ?? ''),
    );

    metricElement.appendChild(metadataElement);

    const chartDataElement = doc.createElement('chartData');

    const dataPoints = getActualDataPoints(parsedMetric);
    dataPoints.forEach((point) => {
      const dataPointElement = doc.createElement('dataPoint');
      addElement(dataPointElement, 'year', String(point.xAxis ?? ''));
      addElement(
        dataPointElement,
        'realValue',
        point.realValue?.toString() ?? '',
      );
      addElement(
        dataPointElement,
        'targetValue',
        point.targetValue?.toString() ?? '',
      );
      addElement(
        dataPointElement,
        'targetValueIndicator',
        point.targetValueIndicator?.toString() ?? '',
      );
      chartDataElement.appendChild(dataPointElement);
    });

    metricElement.appendChild(chartDataElement);
    doc.appendChild(metricElement);

    const serializer = new XMLSerializer();
    const xmlString = serializer.serializeToString(doc);

    const formattedXml = formatXml(xmlString);

    return xmlDeclaration + formattedXml;
  } catch (error) {
    throw new Error(
      `Failed to generate XML: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

function formatXml(xml: string, indent = '  '): string {
  let formatted = '';
  let depth = 0;
  const tokens = xml.trim().replace(/>\s*</g, '>\n<').split('\n');

  for (const token of tokens) {
    if (token.match(/<\/\w/)) {
      depth--;
    }

    formatted += depth > 0 ? indent.repeat(depth) + token + '\n' : token + '\n';

    if (token.match(/<\w[^>]*[^\/]>/) && !token.match(/<\w[^>]*\/>/)) {
      depth++;
    }
  }

  return formatted.trim();
}

export function generateJson(metric: Metric, parsedMetric: ParsedChartReturn) {
  const metaData = {
    id: metric.id ?? '',
    type: metric.typeName ?? '',
    name: metric.titlePlainText ?? '',
    unit: metric.unit ?? '',
    description: metric.descriptionPlainText ?? '',
    dataSource: metric.dataSource ?? '',
    monitoringPeriod: metric.monitoringPeriod ?? '',
    responsibleInstitutionLink: metric.responsibleInstitutionLink ?? '',
    responsibleInstitutionName: metric.responsibleInstitutionName ?? '',
    methodology: metric.methodologyPlainText ?? '',
    code: metric.code ?? '',
    focusArea: metric.focusArea ?? '',
    targetLevel: metric.targetLevel ?? '',
    targetLevelDate: metric.targetLevelDate ?? '',
  };

  const dataPoints = getActualDataPoints(parsedMetric);

  const chartData = dataPoints.map((point) => ({
    year: point.xAxis || '',
    realValue: point.realValue ?? '',
    targetValue: point.targetValue ?? '',
    targetValueIndicator: point.targetValueIndicator ?? '',
  }));

  const jsonObject = {
    metadata: metaData,
    chartData: chartData,
  };

  return JSON.stringify(jsonObject, null, 2);
}

export function downloadFile(
  content: string,
  filename: string,
  mimeType: string,
) {
  const blob = new Blob(['\uFEFF' + content], {
    type: `${mimeType};charset=utf-8`,
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
